/**
 * @module BoardWideGenerateDialog
 * @purpose ETA confirm dialog + per-cluster progress display for board-wide generation (FR7).
 *   No silent multi-hour launch: shows scale + ETA before starting, progress per cluster,
 *   and allows cancel.
 * @depends hooks/use-board-wide-generate, types/reasoning, react-i18next
 * @dependedOnBy BriefingBoardView
 *
 * @rules
 * - Performance: pure display — progress data passed via props from the hook
 * - State: confirm step vs progress step toggled locally
 * - Errors: providerNotConfigured and error shown inline
 */

import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, CheckCircle2, AlertCircle, XCircle } from 'lucide-react'
import type { BoardJobProgress, BoardWideStartParams } from '../../../../../hooks/use-board-wide-generate'

const TOP_N_DEFAULT = 20
const SECS_PER_HOUR = 3600
const SECS_PER_MINUTE = 60

interface BoardWideGenerateDialogProps {
  caseId: string
  runId: string
  jobs: BoardJobProgress[]
  estimateSeconds: number
  generating: boolean
  error: string | null
  providerNotConfigured: boolean
  onStart: (params: BoardWideStartParams) => Promise<void>
  onCancel: () => void
  onClose: () => void
}

const OVERLAY: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
}

const DIALOG: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  padding: '24px',
  width: '480px',
  maxWidth: '90vw',
  maxHeight: '80vh',
  overflowY: 'auto',
}

const TITLE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '1rem',
  color: 'var(--text)',
  fontWeight: 600,
  marginBottom: '12px',
}

const MONO: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.75rem',
}

const BTN_PRIMARY: React.CSSProperties = {
  ...MONO,
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  border: 'none',
  borderRadius: '6px',
  padding: '6px 16px',
  cursor: 'pointer',
  textTransform: 'uppercase',
}

const BTN_GHOST: React.CSSProperties = {
  ...MONO,
  background: 'transparent',
  color: 'var(--text-dim)',
  border: '1px solid var(--border)',
  borderRadius: '6px',
  padding: '6px 16px',
  cursor: 'pointer',
}

function jobStatusIcon(entry: BoardJobProgress): React.ReactElement {
  if (entry.cancelled) return <XCircle className="h-3.5 w-3.5" style={{ color: 'var(--text-dim)' }} />
  if (entry.job.status === 'succeeded') return <CheckCircle2 className="h-3.5 w-3.5" style={{ color: 'green' }} />
  if (entry.job.status === 'failed') return <AlertCircle className="h-3.5 w-3.5" style={{ color: '#dc2626' }} />
  return <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: 'var(--accent)' }} />
}

function buildEtaText(
  estimateSeconds: number,
  jobCount: number,
  t: (key: string, opts: Record<string, number>) => string,
): string {
  if (estimateSeconds <= 0) return ''
  const count = jobCount > 0 ? jobCount : TOP_N_DEFAULT
  if (estimateSeconds >= SECS_PER_HOUR) {
    const hours = Math.ceil(estimateSeconds / SECS_PER_HOUR)
    return count === 1
      ? t('reasoning.briefingBoard.boardWideDialog.eta', { hours, count })
      : t('reasoning.briefingBoard.boardWideDialog.eta_other', { hours, count })
  }
  const minutes = Math.ceil(estimateSeconds / SECS_PER_MINUTE)
  return count === 1
    ? t('reasoning.briefingBoard.boardWideDialog.etaMinutes', { minutes, count })
    : t('reasoning.briefingBoard.boardWideDialog.etaMinutes_other', { minutes, count })
}

interface ConfirmStepProps {
  etaText: string
  error: string | null
  onStart: () => void
  onClose: () => void
  t: (key: string) => string
}

function ConfirmStep({ etaText, error, onStart, onClose, t }: ConfirmStepProps): React.ReactElement {
  return (
    <>
      {etaText && (
        <p style={{ ...MONO, color: 'var(--text-dim)', marginBottom: '16px' }}>{etaText}</p>
      )}
      {error && (
        <p style={{ ...MONO, color: '#dc2626', marginBottom: '12px' }} role="alert">{error}</p>
      )}
      <div className="flex gap-3">
        <button type="button" style={BTN_PRIMARY} onClick={onStart}>
          {t('reasoning.briefingBoard.boardWideDialog.confirm')}
        </button>
        <button type="button" style={BTN_GHOST} onClick={onClose}>
          {t('reasoning.briefingBoard.boardWideDialog.cancel')}
        </button>
      </div>
    </>
  )
}

interface ProgressStepProps {
  jobs: BoardJobProgress[]
  allDone: boolean
  error: string | null
  generating: boolean
  onCancel: () => void
  onClose: () => void
  t: (key: string) => string
}

function ProgressStep({ jobs, allDone, error, generating, onCancel, onClose, t }: ProgressStepProps): React.ReactElement {
  return (
    <>
      <div style={{ ...MONO, color: 'var(--text-dim)', marginBottom: '12px' }}>
        {allDone
          ? t('reasoning.briefingBoard.boardWideDialog.progressDone')
          : t('reasoning.briefingBoard.boardWideDialog.progressTitle')}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px' }}>
        {jobs.map((entry) => (
          <li key={entry.job.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            {jobStatusIcon(entry)}
            <span style={{ ...MONO, color: 'var(--text-dim)', fontSize: '0.68rem' }}>{entry.job.clusterId}</span>
            {entry.job.status === 'failed' && entry.job.errorCategory && (
              <span style={{ ...MONO, color: '#dc2626', fontSize: '0.62rem' }}>({entry.job.errorCategory})</span>
            )}
          </li>
        ))}
      </ul>
      {error && <p style={{ ...MONO, color: '#dc2626', marginBottom: '12px' }} role="alert">{error}</p>}
      <div className="flex gap-3">
        {generating
          ? <button type="button" style={BTN_GHOST} onClick={onCancel}>{t('reasoning.briefingBoard.boardWideDialog.cancelInProgress')}</button>
          : <button type="button" style={BTN_GHOST} onClick={onClose}>{t('reasoning.briefingBoard.boardWideDialog.close')}</button>}
      </div>
    </>
  )
}

/**
 * Modal dialog for board-wide generation ETA confirm and live progress display (FR7).
 *
 * @behaviour Confirm step shows ETA and start/cancel buttons. Once started, transitions
 *   to progress view showing per-cluster job status. Cancel is available throughout.
 *   onClose is called when the user dismisses after completion or cancellation.
 * @errors providerNotConfigured and error displayed inline; never crashes.
 */
export function BoardWideGenerateDialog({
  caseId,
  runId,
  jobs,
  estimateSeconds,
  generating,
  error,
  providerNotConfigured,
  onStart,
  onCancel,
  onClose,
}: BoardWideGenerateDialogProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const [started, setStarted] = useState(false)

  const handleStart = useCallback(async () => {
    setStarted(true)
    await onStart({ caseId, runId, body: { topN: TOP_N_DEFAULT, force: false } })
  }, [caseId, runId, onStart])

  const allDone = started && jobs.length > 0 && jobs.every(
    (entry) => entry.cancelled || entry.job.status === 'succeeded' || entry.job.status === 'failed'
  )

  const etaText = buildEtaText(estimateSeconds, jobs.length, t as (key: string, opts: Record<string, number>) => string)

  if (providerNotConfigured) {
    return (
      <div style={OVERLAY} role="dialog" aria-modal="true">
        <div style={DIALOG}>
          <div style={TITLE_STYLE}>{t('reasoning.briefingBoard.boardWideDialog.title')}</div>
          <p style={{ ...MONO, color: '#dc2626', marginBottom: '16px' }}>
            {t('reasoning.briefingBoard.generate.providerNotConfigured')}
          </p>
          <button type="button" style={BTN_GHOST} onClick={onClose}>
            {t('reasoning.briefingBoard.boardWideDialog.close')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={OVERLAY} role="dialog" aria-modal="true" aria-label={t('reasoning.briefingBoard.boardWideDialog.title')}>
      <div style={DIALOG}>
        <div style={TITLE_STYLE}>{t('reasoning.briefingBoard.boardWideDialog.title')}</div>
        {!started
          ? <ConfirmStep etaText={etaText} error={error} onStart={() => { void handleStart() }} onClose={onClose} t={t as (key: string) => string} />
          : <ProgressStep jobs={jobs} allDone={allDone} error={error} generating={generating} onCancel={onCancel} onClose={onClose} t={t as (key: string) => string} />}
      </div>
    </div>
  )
}
